import { useEffect, useRef, useCallback } from 'react';
import type React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FilterState } from '@/types/filter';
import { FilterType } from '@/models';
import { SectionId } from '../types/filterDraft.types';
import { getFilterableSectionIds, getFilterKeysForSection, getFilterSectionId } from '../utils/sectionHelpers';
import { hasFilterValueChanges } from '../utils/formUtils';

interface UseDraftSyncProps {
  methods: UseFormReturn<FilterState>;
  isVisible: boolean;
  isInitializedRef: React.RefObject<boolean>;
  baselineValues: FilterState;
  defaultValues?: FilterState;
  setSectionDraft: (sectionId: SectionId, data: Partial<FilterState>) => void;
  clearSectionDraft: (sectionId: SectionId) => void;
}

interface UseDraftSyncReturn {
  cancelPendingSync: () => void;
}

function isValidFilterType(key: string): key is FilterType {
  return typeof key === 'string';
}

const extractSectionData = (sectionId: SectionId, allValues: FilterState): Partial<FilterState> | null => {
  const keys: FilterType[] = getFilterKeysForSection(sectionId);
  if (keys.length === 0) return null;

  const draft: Partial<FilterState> = {};
  for (const key of keys) {
    draft[key] = allValues[key];
  }

  return draft;
};

export const useDraftSync = ({
  methods,
  isVisible,
  isInitializedRef,
  baselineValues,
  defaultValues,
  setSectionDraft,
  clearSectionDraft,
}: UseDraftSyncProps): UseDraftSyncReturn => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dirtySectionsRef = useRef<Set<SectionId>>(new Set());

  const processDirtySections = useCallback(() => {
    const dirtySections = Array.from(dirtySectionsRef.current);
    dirtySectionsRef.current.clear();

    if (dirtySections.length === 0) return;

    const currentValues = methods.getValues();

    dirtySections.forEach(sectionId => {
      const sectionKeys = getFilterKeysForSection(sectionId);
      const sectionDraft = extractSectionData(sectionId, currentValues);

      if (sectionDraft && hasFilterValueChanges(sectionKeys, sectionDraft, baselineValues, defaultValues)) {
        setSectionDraft(sectionId, sectionDraft);
      } else {
        clearSectionDraft(sectionId);
      }
    });
  }, [methods, baselineValues, defaultValues, setSectionDraft, clearSectionDraft]);

  const cancelPendingSync = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    dirtySectionsRef.current.clear();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const subscription = methods.watch((_, { name }) => {
      if (!isInitializedRef.current) return;

      if (name && isValidFilterType(name)) {
        const sectionId = getFilterSectionId(name);
        if (sectionId) dirtySectionsRef.current.add(sectionId);
      } else {
        getFilterableSectionIds().forEach(id => dirtySectionsRef.current.add(id));
      }

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(processDirtySections, 300);
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        processDirtySections();
      }
    };
  }, [isVisible, isInitializedRef, methods, processDirtySections]);

  return { cancelPendingSync };
};
